import React from 'react';
import { CheckCircle, AlertCircle, X } from 'react-feather';
import { useChainId } from 'wagmi';
import { toast, ToastContainer, Id } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';

interface TransactionPopupProps {
  hash: string;
  success?: boolean;
  summary?: string;
  onDismiss?: () => void;
}

// Keep track of active toasts globally
const activeToasts = new Set<Id>();

export default function TransactionPopup({
  hash,
  success,
  summary,
  onDismiss
}: TransactionPopupProps) {
  const chainId = useChainId();

  React.useEffect(() => {
    // If this hash already has a toast, don't create another one
    if (activeToasts.has(hash)) {
      return;
    }

    const toastId = toast(
      <div className="flex items-start gap-3">
        <div className="mt-1">
          {success ? (
            <div className="rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FF9900] p-2">
              <CheckCircle className="text-white" size={20} />
            </div>
          ) : (
            <div className="rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FF9900] p-2">
              <AlertCircle className="text-white" size={20} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="font-medium text-white">
            {summary}
          </h3>
          
          <p className="text-body-sm text-white/70">
            {`${hash.slice(0, 8)}...${hash.slice(58, 65)}`}
          </p>

          {chainId && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${config.ftmscanUrl}/tx/${hash}`}
              className="inline-flex items-center gap-1.5 text-body-sm text-white/50 hover:text-white/70 transition-colors mt-1"
            >
              View on Sonicscan
              <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
                <path 
                  d="M3.5 10.5L10.5 3.5M10.5 3.5H4.5M10.5 3.5V9.5" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          )}
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        style: {
          border: "1px solid #e5e7eb",
        },
        className: "bg-gray-800 backdrop-blur-sm border border-[#e5e7eb]",
        progressClassName: "bg-gradient-sonic-horizontal",
        onOpen: () => {
          activeToasts.add(hash);
        },
        onClose: () => {
          activeToasts.delete(hash);
        }
      }
    );

    return () => {
      toast.dismiss(toastId);
      activeToasts.delete(hash);
    };
  }, [hash, success, summary, chainId]);

  return null;
}
